import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  FileText,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import DateRangeSelect from '../../components/DateRangeSelect';
import SalesTransactionSummaryCards, {
  type SalesSummaryCardStatus,
} from '../../components/SalesTransactionSummaryCards';
import SearchableSelect from '../../components/SearchableSelect';
import SortableTableHeader from '../../components/SortableTableHeader';
import TransactionListTotalsBar from '../../components/transactions/TransactionListTotalsBar';
import {
  canManageInvoices,
  canManageQuotations,
} from '../../utils/salesTransactionPermissions';
import { getSalesTransactions } from '../../utils/salesTransactionApi';
import { getCustomers } from '../../utils/customerApi';
import { ApiValidationError } from '../../types/auth';
import type {
  SalesTransaction,
  SalesTransactionSummary,
} from '../../types/salesTransaction';
import type { CustomerPaymentMethod } from '../../types/customerPayment';
import type { Customer } from '../../types/customer';
import type { SearchableSelectOption } from '../../components/SearchableSelect';
import type { PaginationMeta } from '../../types/journalEntry';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  computeSalesTransactionTotals,
  salesTransactionRowKey,
} from '../../utils/salesTransactionSelection';
import { toastRouteSuccess, clearRouteState } from '../../utils/toast';
import { getDateRangeForPreset, type DateRangePreset } from '../../utils/dateRangePresets';
import { useSalesTransactionsHub } from './SalesTransactionsHubContext';
import {
  useSalesTransactionFilters,
  type SortKey,
  type SortDir,
  type StatusFilter,
  type TypeFilter,
  type PaymentMethodFilter,
} from './useSalesTransactionFilters';
import {
  getStatusTabsForType,
  isStatusCompatibleWithType,
} from './salesTransactionFilterParams';

const STATUS_TAB_LABELS: Record<StatusFilter, string> = {
  all: 'All',
  open: 'Open',
  overdue: 'Overdue',
  draft: 'Draft',
  partial: 'Partial',
  paid: 'Paid',
  voided: 'Voided',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  expired: 'Expired',
};

const TYPE_TABS: { id: TypeFilter; label: string }[] = [
  { id: 'all', label: 'All types' },
  { id: 'invoice', label: 'Invoices' },
  { id: 'payment', label: 'Payments' },
  { id: 'quotation', label: 'Quotations' },
];

const PAYMENT_METHOD_OPTIONS: { value: CustomerPaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online' },
];

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const formatTime = (dateString: string) =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const compareTransactions = (
  a: SalesTransaction,
  b: SalesTransaction,
  key: SortKey,
  dir: SortDir
) => {
  const mul = dir === 'asc' ? 1 : -1;

  switch (key) {
    case 'transaction_id': {
      const aId = a.transaction_id;
      const bId = b.transaction_id;
      if (aId == null && bId == null) return 0;
      if (aId == null) return dir === 'asc' ? -1 : 1;
      if (bId == null) return dir === 'asc' ? 1 : -1;
      return mul * (aId - bId);
    }
    case 'transaction_type':
    case 'number':
    case 'customer_name':
    case 'status':
      return mul * a[key].localeCompare(b[key]);
    case 'total_amount':
    case 'balance':
      return mul * (parseFloat(a[key]) - parseFloat(b[key]));
    case 'txn_date':
      return (
        mul *
        (a.txn_date.localeCompare(b.txn_date) ||
          a.created_at.localeCompare(b.created_at))
      );
    case 'payment_method': {
      const aMethod = a.payment_method ?? '';
      const bMethod = b.payment_method ?? '';
      if (!aMethod && !bMethod) return 0;
      if (!aMethod) return 1;
      if (!bMethod) return -1;
      return mul * aMethod.localeCompare(bMethod);
    }
    default:
      return 0;
  }
};

const defaultSortDir = (key: SortKey): SortDir =>
  key === 'transaction_id' ||
    key === 'txn_date' ||
    key === 'total_amount' ||
    key === 'balance'
    ? 'desc'
    : 'asc';

const getStatusColor = (status: string, isOverdue: boolean) => {
  if (isOverdue) return 'bg-orange-100 text-orange-800';
  switch (status.toUpperCase()) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
    case 'POSTED':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    case 'SENT':
      return 'bg-indigo-100 text-indigo-800';
    case 'ACCEPTED':
      return 'bg-green-100 text-green-800';
    case 'REJECTED':
      return 'bg-red-100 text-red-700';
    case 'EXPIRED':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getTypeBadgeClass = (type: SalesTransaction['transaction_type']) => {
  switch (type) {
    case 'INVOICE':
      return 'bg-blue-100 text-blue-800';
    case 'PAYMENT':
      return 'bg-emerald-100 text-emerald-800';
    case 'QUOTATION':
      return 'bg-violet-100 text-violet-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getTypeBadgeLabel = (type: SalesTransaction['transaction_type']) => {
  switch (type) {
    case 'INVOICE':
      return 'Invoice';
    case 'PAYMENT':
      return 'Payment';
    case 'QUOTATION':
      return 'Quotation';
    default:
      return type;
  }
};

const SalesTransactionsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerRefetch } = useSalesTransactionsHub();
  const { filters, listSearch, updateFilters, clearFilters, hasActiveFilters } =
    useSalesTransactionFilters();
  const {
    statusFilter,
    typeFilter,
    paymentMethodFilter,
    customerFilter,
    page,
    sortKey,
    sortDir,
    datePreset,
    startDate,
    endDate,
    searchQuery,
  } = filters;
  const pageRef = useRef(page);
  pageRef.current = page;

  const [transactions, setTransactions] = useState<SalesTransaction[]>([]);
  const [summary, setSummary] = useState<SalesTransactionSummary | null>(null);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [customerOptions, setCustomerOptions] = useState<SearchableSelectOption[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const buildTypeParam = (
    filter: TypeFilter,
    paymentMethod?: PaymentMethodFilter
  ): string | undefined => {
    if (paymentMethod || filter === 'payment') return 'payment';
    if (filter === 'invoice') return 'invoice';
    if (filter === 'quotation') return 'quotation';
    return undefined;
  };

  const statusTabs = useMemo(
    () =>
      getStatusTabsForType(typeFilter).map((id) => ({
        id,
        label: STATUS_TAB_LABELS[id],
      })),
    [typeFilter]
  );

  const hasQuotationRows = useMemo(
    () => transactions.some((txn) => txn.transaction_type === 'QUOTATION'),
    [transactions]
  );

  const handleTypeFilterChange = (nextType: TypeFilter) => {
    const patch: Partial<typeof filters> = {
      typeFilter: nextType,
      page: 1,
    };
    if (nextType === 'invoice' || nextType === 'quotation') {
      patch.paymentMethodFilter = '';
    }
    if (!isStatusCompatibleWithType(statusFilter, nextType)) {
      patch.statusFilter = 'all';
    }
    updateFilters(patch);
  };

  const filteredAndSortedTransactions = useMemo(() => {
    let rows = transactions;
    if (paymentMethodFilter) {
      rows = rows.filter(
        (txn) =>
          txn.transaction_type === 'PAYMENT' && txn.payment_method === paymentMethodFilter
      );
    }
    return [...rows].sort((a, b) => compareTransactions(a, b, sortKey, sortDir));
  }, [transactions, paymentMethodFilter, sortKey, sortDir]);

  const filteredColumnTotals = useMemo(
    () => computeSalesTransactionTotals(filteredAndSortedTransactions),
    [filteredAndSortedTransactions]
  );

  const visibleRowKeys = useMemo(
    () => filteredAndSortedTransactions.map(salesTransactionRowKey),
    [filteredAndSortedTransactions]
  );

  const selectedRows = useMemo(
    () =>
      filteredAndSortedTransactions.filter((txn) =>
        selectedKeys.has(salesTransactionRowKey(txn))
      ),
    [filteredAndSortedTransactions, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeSalesTransactionTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const handleSort = (column: SortKey) => {
    if (sortKey === column) {
      updateFilters({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      updateFilters({ sortKey: column, sortDir: defaultSortDir(column) });
    }
  };

  const fetchTransactions = async (pageNum: number, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    const scrollY = silent ? window.scrollY : null;

    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const typeParam = buildTypeParam(typeFilter, paymentMethodFilter);
      const result = await getSalesTransactions({
        ...(typeParam ? { type: typeParam } : {}),
        status: statusFilter === 'all' ? undefined : statusFilter,
        customer_id: customerFilter ? Number(customerFilter) : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: searchQuery || undefined,
        paid_days: 30,
        per_page: 100,
        page: paymentMethodFilter ? 1 : pageNum,
      });
      setTransactions(result.transactions);
      setSummary(result.summary);
      setMeta(result.meta);
      if (result.meta.current_page !== pageNum) {
        updateFilters({ page: result.meta.current_page });
      }
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setError(firstError || err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch sales transactions');
      }
      setTransactions([]);
      setSummary(null);
      setMeta(null);
    } finally {
      if (silent) {
        setRefreshing(false);
        if (scrollY != null) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.scrollTo({ top: scrollY, left: 0 });
            });
          });
        }
      } else {
        setLoading(false);
      }
    }
  };

  const fetchRef = useRef(fetchTransactions);
  fetchRef.current = fetchTransactions;

  useEffect(() => {
    registerRefetch(() => {
      void fetchRef.current(pageRef.current, { silent: true });
    });
  }, [
    registerRefetch,
    typeFilter,
    statusFilter,
    customerFilter,
    paymentMethodFilter,
    startDate,
    endDate,
    searchQuery,
    datePreset,
    page,
  ]);

  useEffect(() => {
    if (datePreset === 'custom' && (!startDate || !endDate)) {
      return;
    }
    fetchTransactions(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typeFilter,
    statusFilter,
    customerFilter,
    paymentMethodFilter,
    startDate,
    endDate,
    searchQuery,
    datePreset,
    page,
  ]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [
    typeFilter,
    statusFilter,
    customerFilter,
    paymentMethodFilter,
    startDate,
    endDate,
    searchQuery,
    page,
  ]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const result = await getCustomers({
          search: customerSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        setCustomerOptions(
          result.customers.map((c: Customer) => ({ value: String(c.id), label: c.display_name }))
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  useEffect(() => {
    const state = location.state as { successMessage?: string; customerId?: number } | null;
    toastRouteSuccess(state);
    if (state?.successMessage || state?.customerId) {
      clearRouteState();
    }
  }, [location.state, updateFilters]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    updateFilters({ searchQuery: searchInput.trim(), page: 1 });
  };

  const handleDatePresetChange = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      const fallback = getDateRangeForPreset('today');
      updateFilters({
        datePreset: preset,
        startDate: startDate || fallback.startDate,
        endDate: endDate || fallback.endDate,
        page: 1,
      });
      return;
    }

    const range = getDateRangeForPreset(preset);
    updateFilters({
      datePreset: preset,
      startDate: range.startDate,
      endDate: range.endDate,
      page: 1,
    });
  };

  const handleCustomDateRangeChange = (from: string, to: string) => {
    updateFilters({
      datePreset: 'custom',
      startDate: from,
      endDate: to,
      page: 1,
    });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    clearFilters();
  };

  const openView = (txn: SalesTransaction) => {
    if (txn.transaction_type === 'INVOICE') {
      navigate(
        {
          pathname: `view/invoice/${txn.id}`,
          search: listSearch,
        },
        { preventScrollReset: true, state: { availableActions: txn.available_actions } }
      );
      return;
    }

    if (txn.transaction_type === 'QUOTATION') {
      navigate(
        {
          pathname: `view/quotation/${txn.id}`,
          search: listSearch,
        },
        { preventScrollReset: true, state: { availableActions: txn.available_actions } }
      );
      return;
    }

    navigate(
      {
        pathname: `view/payment/${txn.id}`,
        search: listSearch,
      },
      { preventScrollReset: true, state: { availableActions: txn.available_actions } }
    );
  };

  const summaryActiveStatus: SalesSummaryCardStatus | null =
    statusFilter === 'open' ||
      statusFilter === 'overdue' ||
      statusFilter === 'draft' ||
      statusFilter === 'paid'
      ? statusFilter
      : null;

  const handleSummaryCardClick = (status: SalesSummaryCardStatus) => {
    updateFilters({ statusFilter: status, page: 1 });
  };

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData =
    totalsBarMode === 'selected' ? selectedTotals : filteredColumnTotals;
  const totalsBarPageSuffix =
    !paymentMethodFilter && meta && meta.total > filteredColumnTotals.count
      ? ' on this page'
      : '';
  const showTotalsBar = filteredAndSortedTransactions.length > 0;

  return (
    <div className="flex w-full max-w-full flex-1 flex-col min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
              Sales Transactions
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Invoices, customer payments, and quotations in one place
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fetchTransactions(page, { silent: true })}
              disabled={loading || refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading || refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canManageInvoices() && (
              <Link
                to={{ pathname: 'invoice/new', search: listSearch }}
                preventScrollReset
                className="flex items-center justify-center px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Invoice
              </Link>
            )}
            {canManageQuotations() && (
              <Link
                to={{ pathname: 'quotation/new', search: listSearch }}
                preventScrollReset
                className="flex items-center justify-center px-4 py-2 bg-violet-800 text-white rounded-lg hover:bg-violet-700 text-sm font-medium"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Quotation
              </Link>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => fetchTransactions(page)} className="underline shrink-0">
            Retry
          </button>
        </div>
      )}

      <SalesTransactionSummaryCards
        summary={summary}
        loading={loading && !summary}
        activeStatus={summaryActiveStatus}
        countLabel="transaction"
        onCardClick={handleSummaryCardClick}
        className="mb-2"
      />
      {hasQuotationRows && (
        <p className="mb-4 text-xs text-gray-500">Summary totals exclude quotations.</p>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {statusTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              updateFilters({ statusFilter: tab.id, page: 1 });
            }}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${statusFilter === tab.id
                ? 'bg-slate-800 text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <form onSubmit={handleSearch} className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Number, customer, or memo..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
              >
                Search
              </button>
            </div>
          </form>
          <DateRangeSelect
            preset={datePreset}
            startDate={startDate}
            endDate={endDate}
            onPresetChange={handleDatePresetChange}
            onCustomRangeChange={handleCustomDateRangeChange}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <SearchableSelect
              options={customerOptions}
              value={customerFilter}
              onChange={(val) => {
                updateFilters({ customerFilter: val ? String(val) : '', page: 1 });
              }}
              onSearchChange={setCustomerSearchInput}
              isLoading={customerSearchLoading}
              placeholder="All customers"
              searchPlaceholder="Search customers..."
              clearValue=""
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTypeFilterChange(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full ${typeFilter === tab.id
                  ? 'bg-slate-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              {tab.label}
            </button>
          ))}
          {(typeFilter === 'all' || typeFilter === 'payment') && (
            <>
              <span className="hidden sm:inline text-gray-300 px-1">|</span>
              <button
                type="button"
                onClick={() => {
                  updateFilters({ paymentMethodFilter: '', page: 1 });
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-full ${paymentMethodFilter === ''
                    ? 'bg-emerald-800 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                All methods
              </button>
              {PAYMENT_METHOD_OPTIONS.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => {
                    updateFilters({
                      paymentMethodFilter: method.value,
                      typeFilter: typeFilter === 'all' ? 'payment' : typeFilter,
                      page: 1,
                    });
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full ${paymentMethodFilter === method.value
                      ? 'bg-emerald-800 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {method.label}
                </button>
              ))}
            </>
          )}
          {hasActiveFilters && (
            <>
              <Filter className="h-4 w-4 text-gray-400 ml-1" />
              <span className="text-sm text-gray-500">
                {paymentMethodFilter
                  ? `${filteredColumnTotals.count} results`
                  : `${meta?.total ?? transactions.length} results`}
                {filteredColumnTotals.count > 0 && (
                  <>
                    {' · '}
                    Total {formatCurrency(filteredColumnTotals.totalAmount)}
                    {filteredColumnTotals.balance > 0 && (
                      <> · Balance {formatCurrency(filteredColumnTotals.balance)}</>
                    )}
                  </>
                )}
              </span>
              <button onClick={handleClearFilters} className="text-sm text-slate-700 underline">
                Clear
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`bg-white rounded-lg shadow overflow-hidden${refreshing ? ' opacity-80' : ''}`}>
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-slate-800"
                    aria-label="Select all on this page"
                  />
                </th>
                <SortableTableHeader label="ID" column="transaction_id" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTableHeader label="Date" column="txn_date" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTableHeader label="Type" column="transaction_type" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTableHeader label="Number" column="number" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTableHeader label="Customer" column="customer_name" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTableHeader label="Method" column="payment_method" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTableHeader label="Total" column="total_amount" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <SortableTableHeader label="Balance" column="balance" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                <SortableTableHeader label="Status" column="status" currentSort={sortKey} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAndSortedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <FileText className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">No transactions found.</p>
                  </td>
                </tr>
              ) : (
                filteredAndSortedTransactions.map((txn) => {
                  const isQuotation = txn.transaction_type === 'QUOTATION';
                  const balance = parseFloat(txn.balance);
                  const isUnappliedPayment =
                    txn.transaction_type === 'PAYMENT' && balance > 0;
                  const statusLabel = isUnappliedPayment
                    ? 'Unapplied'
                    : txn.is_overdue
                      ? 'Overdue'
                      : formatLabel(txn.status);
                  const statusColor = isUnappliedPayment
                    ? 'bg-amber-100 text-amber-800'
                    : getStatusColor(txn.status, txn.is_overdue);
                  const rowKey = salesTransactionRowKey(txn);
                  const isSelected = selectedKeys.has(rowKey);
                  return (
                    <tr
                      key={rowKey}
                      className={`hover:bg-gray-50 cursor-pointer${isSelected ? ' bg-slate-50/60' : ''}`}
                      onClick={() =>
                        txn.available_actions.includes('view') && openView(txn)
                      }
                    >
                      <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleRow(rowKey, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-slate-800"
                          aria-label={`Select ${txn.number}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono tabular-nums">
                        {txn.transaction_id ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>{formatDate(txn.txn_date)}</div>
                        {txn.created_at && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {formatTime(txn.created_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTypeBadgeClass(txn.transaction_type)}`}
                        >
                          {getTypeBadgeLabel(txn.transaction_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 font-mono">
                        {txn.number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{txn.customer_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {txn.payment_method ? formatLabel(txn.payment_method) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        {formatCurrency(txn.total_amount)}
                      </td>
                      <td
                        className={`px-4 py-3 text-sm text-right font-medium ${
                          isQuotation
                            ? 'text-gray-400'
                            : balance > 0
                              ? 'text-red-600'
                              : 'text-green-600'
                        }`}
                      >
                        {isQuotation ? '—' : formatCurrency(txn.balance)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor}`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
            </div>
          </>
        )}
      </div>

      {meta && meta.last_page > 1 && !paymentMethodFilter && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-sm text-gray-500">
            Page {meta.current_page} of {meta.last_page} · {meta.total} total
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => updateFilters({ page: meta.current_page - 1 })}
              disabled={meta.current_page <= 1 || loading}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => updateFilters({ page: meta.current_page + 1 })}
              disabled={meta.current_page >= meta.last_page || loading}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
      </div>

      {showTotalsBar && !loading && (
        <TransactionListTotalsBar
          mode={totalsBarMode}
          count={totalsBarData.count}
          totalAmount={totalsBarData.totalAmount}
          balance={totalsBarData.balance}
          pageCountSuffix={totalsBarPageSuffix}
          onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
        />
      )}
    </div>
  );
};

export default SalesTransactionsList;
